import { useEffect, useRef, useState } from 'react';
import { AudioLines, Volume2 } from 'lucide-react';
import api from '@/lib/api';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import {
  computeWaveformPeaks,
  quietFloorDeltaDb,
  waveformBarHeights,
  waveformBarHeightsDb,
  waveformRemovedRms,
} from '@/lib/audioWaveform';

async function arrayBufferForAudio(url) {
  const playback = mediaUrlForPlayback(url);
  if (!playback) throw new Error('no audio url');
  if (playback.startsWith('/api/') || playback.includes('/api/media/')) {
    const apiPath = playback.startsWith('/')
      ? playback
      : playback.replace(/^https?:\/\/[^/]+/, '');
    const { data } = await api.get(apiPath, { responseType: 'arraybuffer' });
    return data;
  }
  const response = await fetch(playback, { credentials: 'include' });
  if (!response.ok) throw new Error(`fetch ${response.status}`);
  return response.arrayBuffer();
}

async function peaksFromUrl(url, barCount) {
  const arrayBuffer = await arrayBufferForAudio(url);
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) throw new Error('AudioContext unavailable');
  const context = new AudioContextClass();
  try {
    const buffer = await context.decodeAudioData(arrayBuffer.slice(0));
    const channel = buffer.getChannelData(0);
    return computeWaveformPeaks(channel, barCount);
  } finally {
    await context.close?.();
  }
}

function WaveformBars({ heights, color, progress = 0 }) {
  return (
    <div className="relative h-14 w-full overflow-hidden rounded bg-black/40">
      <div className="absolute inset-0 flex items-center gap-px px-0.5">
        {(heights || []).map((h, index) => (
          <span
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className="min-w-[1px] flex-1 rounded-sm"
            style={{
              height: `${Math.max(8, h * 100)}%`,
              background: color,
              opacity: index / Math.max(1, heights.length) < progress ? 1 : 0.42,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function SingleTrack({
  src,
  label,
  color,
  icon: Icon = Volume2,
  heights,
  quietDb,
  loading,
  failed,
  footer,
  children,
}) {
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setProgress(0);
  }, [src]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] uppercase tracking-widest" style={{ color }}>
          {label}
        </p>
        {Number.isFinite(quietDb) ? (
          <span className="text-[10px] text-slate-500 font-mono">
            pauses {quietDb.toFixed(0)} dB
          </span>
        ) : null}
      </div>
      {loading ? (
        <div className="h-14 rounded bg-black/40 animate-pulse" />
      ) : failed ? (
        <p className="text-[10px] text-slate-600 h-14 flex items-center">Waveform unavailable</p>
      ) : (
        <WaveformBars heights={heights} color={color} progress={progress} />
      )}
      <div className="flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
        <audio
          key={src}
          ref={audioRef}
          src={src}
          controls
          className="w-full h-8"
          onTimeUpdate={(e) => {
            const el = e.currentTarget;
            const dur = Number(el.duration) || 0;
            setProgress(dur > 0 ? el.currentTime / dur : 0);
          }}
        />
      </div>
      {footer}
      {children}
    </div>
  );
}

export function DualSoundtrackCompare({
  originalSrc,
  cleanedSrc,
  projectId,
  snippetId,
  choice,
  onChoose,
  chooseDisabled = false,
  cleanSummary = '',
  choiceTestId,
}) {
  const [original, setOriginal] = useState(null);
  const [cleaned, setCleaned] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    if (!originalSrc && !cleanedSrc && !projectId) {
      setOriginal(null);
      setCleaned(null);
      setFailed(false);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setFailed(false);

    const fromApi = (row) => {
      if (!row || !Array.isArray(row.rms)) return null;
      return {
        peaks: row.peaks || [],
        rms: row.rms,
        quietDb: row.quiet_db ?? row.quietDb,
      };
    };

    const timer = window.setTimeout(() => {
      (async () => {
        try {
          if (cancelled) return;
          if (projectId && snippetId) {
            try {
              const { data } = await api.get(
                `/reels-shorts/projects/${projectId}/snippets/${snippetId}/waveform`,
                { params: { bars: 168 }, timeout: 45_000, signal: controller.signal },
              );
              if (cancelled) return;
              setOriginal(fromApi(data.original));
              setCleaned(fromApi(data.cleaned));
              setLoading(false);
              return;
            } catch (err) {
              if (cancelled || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
              /* fall through to client decode */
            }
          }
          if (cancelled) return;
          const [origPeaks, cleanPeaks] = await Promise.allSettled([
            originalSrc ? peaksFromUrl(originalSrc, 168) : Promise.resolve(null),
            cleanedSrc ? peaksFromUrl(cleanedSrc, 168) : Promise.resolve(null),
          ]);
          if (cancelled) return;
          setOriginal(origPeaks.status === 'fulfilled' ? origPeaks.value : null);
          setCleaned(cleanPeaks.status === 'fulfilled' ? cleanPeaks.value : null);
          setFailed(origPeaks.status === 'rejected' && cleanPeaks.status === 'rejected');
          setLoading(false);
        } catch {
          if (!cancelled) {
            setFailed(true);
            setLoading(false);
          }
        }
      })();
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [originalSrc, cleanedSrc, projectId, snippetId]);

  const originalHeights = original ? waveformBarHeightsDb(original.rms, -48) : [];
  const cleanedHeights = cleaned ? waveformBarHeightsDb(cleaned.rms, -48) : [];
  const removedRms = waveformRemovedRms(original?.rms, cleaned?.rms);
  const removedHeights = removedRms.length ? waveformBarHeights(removedRms) : [];
  const delta = quietFloorDeltaDb(original?.quietDb, cleaned?.quietDb);

  if (!originalSrc) {
    return <p className="text-[11px] text-slate-600">No separate audio yet</p>;
  }

  return (
    <div className="space-y-3">
      <SingleTrack
        src={originalSrc}
        label="Original MP3"
        color="#94a3b8"
        heights={originalHeights}
        quietDb={original?.quietDb}
        loading={loading}
        failed={failed && !original}
      />
      {cleanedSrc ? (
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-2 space-y-2">
          <SingleTrack
            src={cleanedSrc}
            label={cleanSummary ? `Cleaned MP3 · ${cleanSummary}` : 'Cleaned MP3'}
            color="#34d399"
            icon={AudioLines}
            heights={cleanedHeights}
            quietDb={cleaned?.quietDb}
            loading={loading}
            failed={failed && !cleaned}
            footer={
              <>
                {removedHeights.length ? (
                  <div className="space-y-1 pt-1">
                    <p className="text-[10px] uppercase tracking-widest text-amber-400/80">
                      Energy removed
                    </p>
                    <WaveformBars heights={removedHeights} color="#fbbf24" />
                  </div>
                ) : null}
                {Number.isFinite(delta) ? (
                  <p className="text-[10px] text-slate-400 leading-snug">
                    {delta >= 2.5
                      ? `Pauses are about ${delta.toFixed(0)} dB quieter after cleaning. Speech loudness is matched on purpose — compare the amber strip and the pause dB numbers, not the speech peaks.`
                      : 'Loudness is matched, so speech peaks look similar. The amber strip is what cleaning took out (mostly pauses / room noise).'}
                  </p>
                ) : (
                  <p className="text-[10px] text-slate-500 leading-snug">
                    Waves use a dB scale so quiet gaps are visible. Speech peaks stay similar because both files are loudness-matched.
                  </p>
                )}
              </>
            }
          />
          {onChoose ? (
            <div
              className="flex flex-wrap gap-3 pt-0.5"
              data-testid={choiceTestId}
            >
              <label className="flex items-center gap-1.5 text-[11px] text-slate-300 cursor-pointer">
                <input
                  type="radio"
                  name={choiceTestId || 'audio-choice'}
                  value="original"
                  checked={choice === 'original'}
                  disabled={chooseDisabled}
                  onChange={() => onChoose('original')}
                  className="accent-slate-400"
                />
                Use original
              </label>
              <label className="flex items-center gap-1.5 text-[11px] text-emerald-200 cursor-pointer">
                <input
                  type="radio"
                  name={choiceTestId || 'audio-choice'}
                  value="cleaned"
                  checked={choice === 'cleaned'}
                  disabled={chooseDisabled}
                  onChange={() => onChoose('cleaned')}
                  className="accent-emerald-400"
                />
                Use cleaned
              </label>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
